import { ErrorBoundary } from "react-error-boundary";
import { LoadingState } from "./scene/LoadingState";
import { Canvas } from "@react-three/fiber";
import { Suspense, useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { SceneSetup } from "./scene/SceneSetup";
import { useSceneSetup } from "../hooks/useSceneSetup";
import { FootballField, FootballPlayer, FootballBall } from "./scene/SceneComponents";
import * as THREE from "three";

interface SceneProps {
  drillText?: string;
  highQuality?: boolean;
}

const Scene = ({ drillText, highQuality = false }: SceneProps) => {
  console.log("Scene component rendering with drill text:", drillText);
  console.log("High quality rendering:", highQuality);
  
  const { isLoading, error, players } = useSceneSetup(drillText);
  const [sceneReady, setSceneReady] = useState(false);

  useEffect(() => {
    console.log("Scene component mounted");
    return () => {
      console.log("Scene component unmounted");
    };
  }, []);

  useEffect(() => {
    console.log("Players data updated:", players);
  }, [players]);

  if (error) {
    console.error("Scene setup error:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "Failed to set up the scene. Please try again.",
    });
    return null;
  }

  return (
    <ErrorBoundary
      fallback={
        <div className="w-full h-full flex items-center justify-center bg-red-50 text-red-500">
          <p>Something went wrong with the 3D scene</p>
        </div>
      }
      onError={(error) => console.error("3D scene error boundary caught:", error)}
    >
      <div className="w-full h-full relative">
        <Canvas
          dpr={highQuality ? [1.5, 2.5] : [1, 2]}
          gl={{ 
            preserveDrawingBuffer: true,
            antialias: true,
            alpha: true,
            powerPreference: highQuality ? "high-performance" : "default",
            precision: highQuality ? "highp" : "mediump"
          }}
          shadows={highQuality}
          camera={{
            position: [0, 15, 25],
            fov: 75,
            near: 0.1,
            far: 1000
          }}
          onCreated={({ gl, scene }) => {
            console.log("Canvas created with quality:", highQuality ? "high" : "standard");
            gl.setClearColor('#87CEEB', 0);
            
            if (highQuality) {
              scene.background = null;
              gl.setPixelRatio(window.devicePixelRatio);
              gl.toneMapping = THREE.ACESFilmicToneMapping;
              gl.toneMappingExposure = 1.2;
              console.log("Applied high-performance rendering settings");
            }
            
            setSceneReady(true);
          }}
        >
          <Suspense fallback={null}>
            <SceneSetup enhancedLighting={highQuality} />
            {!isLoading && sceneReady && (
              <>
                <FootballField enhancedTextures={highQuality} />
                {players.map((player, index) => (
                  <FootballPlayer
                    key={index}
                    position={[player.position.x, player.position.y, player.position.z]}
                    path={player.path?.map(p => [p.x, p.y, p.z])}
                    enhancedModel={highQuality}
                  />
                ))}
                <FootballBall position={[0, 1, 0]} enhancedModel={highQuality} />
              </>
            )}
          </Suspense>
        </Canvas>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 backdrop-blur-sm">
            <LoadingState message={highQuality ? "Loading high-quality 3D assets..." : "Loading 3D assets..."} />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Scene;
